import { type PlaybackController } from 'components/VideoPlayer/controller/PlaybackController';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import { useControllerState } from 'solox';
import { useDelayed } from 'src/hooks/useDelayed';
import { useSelectedStream } from './useSelectedStream';

const BLUR_ON_LOADING_THRESHOLD_MS = 1000;

export type LoadingReason =
  | 'WAITING_FOR_PLAYERSTATE'
  | 'NO_STREAM_AVAILABLE'
  | 'WAITING_FOR_PLAYLIST'
  | 'PLAYER_WAITING'
  | 'SEEKING';

/**
 * Work out whether or not the video stream is currently playing or not.  If the
 * playlist is loading, or the video player is waiting for data, or the video
 * player is in the middle of a seek, or for some other reason the video player
 * isn't actually playing video when it's supposed to be, this will return a
 * string describing what's going on.
 *
 * This returns two values - a boolean indicating whether we should show a loading
 * spinner or not, and a
 * Right now the returned string is just for debugging purposes - we could translate
 * this and show it to the client though.
 */
export function useVideoPlayerIsLoading(
  playbackController: PlaybackController,
  playingCamera: PlayingCamera,
): [boolean, LoadingReason | undefined] {
  const selectedStream = useSelectedStream(playbackController, playingCamera.playerId);

  const reason =
    useControllerState(playbackController.state, (state) => {
      const playingLive = playbackController.isPlayingLive();

      const playerState = state.byPlayerId[playingCamera.playerId];
      if (!playerState) {
        return 'WAITING_FOR_PLAYERSTATE';
      } else if (
        !playingLive &&
        (!playerState.state.playlist || playerState.state.playlist.state === 'loading')
      ) {
        return 'WAITING_FOR_PLAYLIST';
      } else if (playerState.status?.state === 'waiting') {
        return 'PLAYER_WAITING';
      } else if (!playingLive && playerState.state.seekRequired) {
        return 'SEEKING';
      }
    }) || (!selectedStream ? 'NO_STREAM_AVAILABLE' : undefined);

  // If the video player is seeking/buffering, then we want to
  // show a loading circle, so it's obvious the video isn't
  // playing.  But, we want to wait a short delay in these cases, because otherwise
  // in some situations, like if we're playing a 16x on a high bitrate camera,
  // we'll get a brief flash of the loading circle every few moments.
  // But, if the video isn't playing because there's no stream or because
  // there's no playlist, show the loading circle right away.
  const loadingDelayMs =
    reason === 'WAITING_FOR_PLAYERSTATE' ||
    reason === 'NO_STREAM_AVAILABLE' ||
    reason === 'WAITING_FOR_PLAYLIST'
      ? 0
      : BLUR_ON_LOADING_THRESHOLD_MS;
  const delayedLoading = useDelayed(reason ? loadingDelayMs : undefined);

  return [delayedLoading, reason];
}
